<template>
  <div>
    <select-test-session-dialog
      key="diagnosticResultsDialog"
      :visible.sync="events.selectDialog"
      :filters="filters"
      :events="events"
      :protocols="protocol.items"
      @select:event="selectTestSession($event)"
      @update:filter="fetchEventsDebounced"
      @click:addNewTestSession="addNewTestSession"
    />

    <card-dialog
      v-model="player.selectDialog"
      :title="$t('select_player.dialog.header')"
      max-width="500px"
    >
      <v-card-text
        class="pa-6"
      >
        <v-text-field
          v-model="player.search"
          class="mb-2"
          dense
          outlined
          hide-details
          append-icon="fal fa-search"
          :placeholder="$t('text_input.search_players.title')"
        />

        <div
          v-if="diagnosticResults.loading"
          class="d-flex justify-center align-center"
          style="min-height: 300px"
        >
          <v-progress-circular
            indeterminate
            size="100"
            width="8"
            color="grey lighten-2"
          />
        </div>
        <div v-else>
          <div
            v-if="diagnosticResults.data.length"
            class="d-flex justify-end mb-1"
          >
            <v-btn
              text
              class="text-none"
              small
              color="grey"
              @click="diagnosticsEventDeleteConfirmationDialogVisible = true"
            >
              <v-icon
                x-small
                left
              >
                fal fa-trash
              </v-icon>
              {{ $t("button.clear_diagnostics_data") }}
            </v-btn>
          </div>
          <v-list
            class="v-list--bordered pa-0 ma-0"
          >
            <v-list-item
              v-for="p in filteredPlayerItems"
              :key="p.id"
              @click="selectPlayer(p)"
            >
              <v-list-item-avatar>
                <v-avatar
                  size="32"
                  class="mr-2"
                  color="grey lighten-2"
                >
                  <v-img :src="p.photo_url">
                    <template v-slot:placeholder>
                      <v-icon x-small>
                        fal fa-user
                      </v-icon>
                    </template>
                  </v-img>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-title>
                {{ p.first_name }} {{ p.last_name }}
              </v-list-item-title>
              <v-tooltip
                v-if="p.diagnostic_results && p.diagnostic_results.length > 0"
                top
              >
                <template v-slot:activator="{ on }">
                  <v-list-item-action v-on="on">
                    <v-avatar
                      color="success"
                      size="28px"
                      class="mr-4"
                    >
                      <v-icon
                        color="white"
                        small
                      >
                        fa fa-check
                      </v-icon>
                    </v-avatar>
                  </v-list-item-action>
                </template>
                <span>
                  Submitted on {{ $dayjs(p.diagnostic_results[p.diagnostic_results.length - 1].created_at).format(getDateTimeFormatForUser('dddd, MMMM D, HH:mm:ss')) }}
                </span>
              </v-tooltip>
              <v-list-item-action>
                <v-icon>
                  fal fa-arrow-right
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>
        <delete-confirmation-dialog
          :visible.sync="diagnosticsEventDeleteConfirmationDialogVisible"
          :submitting="deletingDiagnostics"
          :text="$t('delete_confirm.clear_event_diagnostics_data')"
          :cta-text="$t('delete_confirm.yes_clear')"
          @confirmed="destroyEventDiagnostics()"
        />
      </v-card-text>
    </card-dialog>

    <card-dialog
      v-model="results.dialog"
      max-width="900px"
      :title="$t('enter_diagnostic_results.dialog.header')"
    >
      <v-card-text
        v-if="protocol.selected && player.selected"
        class="pa-6"
      >
        <div class="rounded-lg grey-bg pa-2 mb-2">
          <v-row
            no-gutters
            align="center"
          >
            <v-col
              cols="3"
              class="px-4 font-weight-medium"
            >
              {{ $t('test_session.label.title') }}
            </v-col>
            <v-col>
              {{ events.selected.diagnostic_protocol.name }} on {{ $dayjs(events.selected.start_at).format(getDateTimeFormatForUser('L HH:mm')) }}
            </v-col>
          </v-row>
        </div>
        <div class="rounded-lg grey-bg pa-2 mb-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col
              cols="3"
              class="px-4 font-weight-medium"
            >
              {{ $t("player.label.title") }}
            </v-col>
            <v-col>
              {{ player.selected.first_name }} {{ player.selected.last_name }}
            </v-col>
          </v-row>
        </div>

        <v-row
          class="mb-2"
          no-gutters
        >
          <v-spacer />
          <v-col cols="auto">
            <v-btn
              text
              class="text-none"
              small
              color="grey"
              @click="diagnosticsDeleteConfirmationDialogVisible = true"
            >
              <v-icon
                x-small
                left
              >
                fal fa-trash
              </v-icon>
              {{ $t("button.clear_diagnostics_data") }}
            </v-btn>
          </v-col>
        </v-row>

        <validation-observer ref="testResultsValidation">
          <div>
            <v-card
              v-for="(testGroup, index) in resultVariablesGrouped"
              :key="index"
              outlined
              class="rounded-lg pa-3 mb-3"
            >
              <v-card-title class="body-2 px-2 pt-3">
                {{ testGroup.test.test_name }}
              </v-card-title>

              <v-card-text v-if="testGroup.test.is_vald">
                <v-select
                  v-model="testGroup.data_url"
                  label="VALD testing"
                  :items="getValdMeasurementsForTest(testGroup.test.test_id)"
                  :loading="valdMeasurements.loading"
                  item-text="start_time"
                  item-value="s3_bucket_url"
                  outlined
                  dense
                  clearable
                  style="max-width: 400px"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ $dayjs(item.start_time).format(getDateTimeFormatForUser('dddd, MMMM D, HH:mm:ss')) }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ item.test_type }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span>{{ $dayjs(item.start_time).format(getDateTimeFormatForUser('dddd, MMMM D, HH:mm:ss')) }}: {{ item.test_type }}</span>
                  </template>
                </v-select>
              </v-card-text>
              <v-card-text v-else>
                <div
                  v-for="variable in testGroup.variables"
                  :key="variable.id"
                >
                  <v-row>
                    <v-col>
                      <div class="text-body-2">
                        {{ variable.var }}
                      </div>
                      <div
                        v-if="variable.input_method_code === 'AM'"
                        class="text-caption"
                      >
                        {{ $t('diagnostic_results_input.leave_empty.hint') }}
                      </div>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col
                      v-for="input in variable.input"
                      :key="variable.id + '_' + input.rep"
                    >
                      <validation-provider
                        v-slot="{ errors }"
                        :rules="{required: emptyInput}"
                      >
                        <template v-if="variable.unit === 'grade'">
                          <v-btn-toggle
                            v-model="input.value"
                            color="primary"
                            class="mb-4"
                          >
                            <template v-for="i in range(variable.grade_def.min, variable.grade_def.max, variable.grade_def.step)">
                              <v-btn
                                :key="'grade'+variable.id+input.rep+i"
                                :value="i"
                              >
                                {{ i }}
                              </v-btn>
                            </template>
                          </v-btn-toggle>
                        </template>
                        <template v-else>
                          <number-input
                            v-model="input.value"
                            outlined
                            dense
                            :placeholder="variable.input.length > 1 ? 'Rep ' + input.rep : $t('label.e_g') + ' ' + 10"
                            :suffix="variable.unit"
                            :decimal-precision="2"
                            :error-messages="errors"
                            style="max-width: 400px"
                          />
                        </template>
                      </validation-provider>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </validation-observer>

        <delete-confirmation-dialog
          :visible.sync="diagnosticsDeleteConfirmationDialogVisible"
          :submitting="deletingDiagnostics"
          :text="$t('delete_confirm.clear_player_diagnostics_data')"
          :cta-text="$t('delete_confirm.yes_clear')"
          @confirmed="destroyPlayerDiagnostics()"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          {{ $t("button.save_results") }}
        </v-btn>
      </v-card-actions>
    </card-dialog>
  </div>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import { DiagnosticProtocolApiService, PlayerApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import { EventApiService } from '@/data/services/EventApiService'
import { DiagnosticResultApiService } from '@/data/services/DiagnosticResultApiService'
import { EventType, EventTypes } from '@/data/models'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import debounce from 'lodash/debounce'
import dayjs from 'dayjs'
import SelectTestSessionDialog from '@/components/dialogs/SelectTestSessionDialog.vue'
import NumberInput from '@/components/helpers/NumberInput.vue'
import { ValdMeasurementApiService } from '@/data/services/ValdMeasurementApiService'
import { getDateTimeFormatForUser } from '@/utils/helpers'

export default {
  name: 'DiagnosticResultsDialog',
  components: { NumberInput, SelectTestSessionDialog, DeleteConfirmationDialog, CardDialog },
  data() {
    return {
      events: {
        selectDialog: false,
        data: [],
        loading: false,
        selected: null
      },
      protocol: {
        selectDialog: false,
        items: [],
        itemsLoading: false,
        selected: null,
        search: ''
      },
      player: {
        teamId: 'all',
        search: '',
        selectDialog: false,
        items: [],
        itemsLoading: false,
        selected: null
      },
      results: {
        dialog: false,
        variables: [],
        id: undefined
      },
      diagnosticResults: {
        data: [],
        loading: false
      },
      valdMeasurements: {
        data: [],
        loading: false
      },
      submitting: false,
      diagnosticsDeleteConfirmationDialogVisible: false,
      diagnosticsEventDeleteConfirmationDialogVisible: false,
      deletingDiagnostics: false,
      filters: {
        eventType: undefined,
        dateFrom: dayjs().subtract(2, 'week').format('YYYY-MM-DD'),
        dateTo: dayjs().format('YYYY-MM-DD'),
        name: '',
        diagnosticProtocolId: null
      },
      filterDateFromActive: false,
      filterDateToActive: false
    }
  },
  computed: {
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    filteredPlayerItems() {
      if (!this.events.selected) return []

      const players = this.player.items.filter(p => this.events.selected.player_ids.includes(p.id)).map(p => {
        p.diagnostic_results = this.diagnosticResults.data.filter(r => r.player_id === p.id)

        return p
      })

      if (!this.player.search) return players

      return players.filter(p => {
        return (p.first_name + ' ' + p.last_name).toLowerCase().indexOf(this.player.search.toLowerCase()) > -1
      })
    },
    resultVariablesGrouped() {
      const testGroups = {}

      this.results.variables.forEach(variable => {
        const testId = variable.test.id

        if (!testGroups[testId]) {
          testGroups[testId] = {
            test: variable.test,
            data_url: this.diagnosticResults.data.find(r => r.id === this.results.id)?.tests.find(t => t.id === testId)?.data_url || 0,
            variables: []
          }
        }
        testGroups[testId].variables.push(variable)
      })

      return Object.values(testGroups)
    },
    emptyInput() {
      return this.results.variables.map(v => {
        return v.input.map(i => {
          return i.value === null || i.value === ''
        })
      }).flat().every(v => v)
    }
  },
  created() {
    this.eventTypeTestSession = EventType.TEST_SESSION
    this.eventTypeReadiness = EventType.READINESS

    this.eventTypes = EventTypes

    EventBus.$on(this.$events.OPEN_DIALOG_DIAGNOSTIC_RESULTS, () => {
      this.events.selectDialog = true
      this.events.selected = null
      this.results.id = undefined
      this.results.variables = []
      this.protocol.selected = null

      this.filters = {
        eventType: undefined,
        dateFrom: dayjs().subtract(2, 'week').format('YYYY-MM-DD'),
        dateTo: dayjs().format('YYYY-MM-DD'),
        name: '',
        diagnosticProtocolId: null
      }

      this.fetchEvents()
      this.fetchAllProtocols()
      this.fetchAllPlayers()
    })

    EventBus.$on(this.$events.PROTOCOL_CREATED, () => {
      this.fetchAllProtocols()
      this.fetchEvents()
    })

    EventBus.$on(this.$events.PROTOCOL_UPDATED, () => {
      this.fetchAllProtocols()
      this.fetchEvents()
    })

    EventBus.$on(this.$events.PROTOCOL_DELETED, () => {
      this.fetchAllProtocols()
      this.fetchEvents()
    })

    EventBus.$on(this.$events.EVENT_CREATED, () => {
      this.fetchEvents()
    })

    EventBus.$on(this.$events.EVENT_UPDATED, () => {
      this.fetchEvents()
    })

    EventBus.$on(this.$events.DIAGNOSTIC_RESULT_CREATED, () => {
      this.fetchDiagnosticResults()
    })

    EventBus.$on(this.$events.DIAGNOSTIC_RESULT_UPDATED, () => {
      this.fetchDiagnosticResults()
    })

    EventBus.$on(this.$events.DIAGNOSTIC_RESULT_DELETED, () => {
      this.fetchDiagnosticResults()
    })

    EventBus.$on(this.$events.EDIT_DIAGNOSTIC_RESULT, (testResult) => {
      this.events.selected = testResult.event
      this.events.selected.diagnostic_protocol = testResult.diagnostic_protocol
      this.player.selected = testResult.player
      this.protocol.selected = testResult.diagnostic_protocol
      this.results.variables = testResult.variables
      this.results.id = testResult.id

      this.results.dialog = true
    })
  },
  methods: {
    getDateTimeFormatForUser,
    async fetchAllProtocols() {
      try {
        this.protocol.itemsLoading = true

        const result = await DiagnosticProtocolApiService.getAll({})

        this.protocol.items = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_protocols.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.protocol.itemsLoading = true
      }
    },
    async fetchEvents() {
      try {
        this.events.loading = true

        const result = await EventApiService.getAll({
          finished: false,
          types: [EventType.TEST_SESSION, EventType.READINESS],
          orderBy: 'end_at',
          order: 'desc',
          search: this.filters.name,
          type: this.filters.eventType,
          from: this.filters.dateFrom,
          to: this.filters.dateTo,
          diagnosticProtocolId: this.filters.diagnosticProtocolId
        })

        this.events.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_test_sessions.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.events.loading = false
      }
    },
    async fetchAllPlayers() {
      try {
        this.player.itemsLoading = true

        const result = await PlayerApiService.getSimpleList({ })

        this.player.items = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_players.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.player.itemsLoading = true
      }
    },
    async fetchValdMeasurements() {
      try {
        this.valdMeasurements.loading = true

        const result = await ValdMeasurementApiService.getAll({
          player_id: this.player.selected.id
        })

        this.valdMeasurements.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_vald_measurements.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.valdMeasurements.loading = false
      }
    },
    fetchEventsDebounced: debounce(function () {
      this.fetchEvents()
    }, 500),
    prepareTestsInput() {
      if (!this.protocol.selected) return

      const lastTestResult = this.diagnosticResults.data.find(r => r.player_id === this.player.selected.id)

      this.results.id = lastTestResult ? lastTestResult.id : null

      this.results.variables = this.protocol.selected.variables.map(variable => {
        const inputVariables = []

        for (let i = 0; i < variable.mes_rep; i++) {
          let value = null

          if (lastTestResult) {
            value = lastTestResult.variables.find(oldv => oldv.var_id === variable.var_id)?.input.find(oldi => oldi.rep === i + 1)?.value
          }

          inputVariables.push({
            rep: i + 1,
            value: value
          })
        }

        return {
          id: variable.id,
          var: variable.var,
          var_id: variable.var_id,
          var_name: variable.var_name,
          unit: variable.unit,
          grade_def: variable.grade_def,
          input_method_code: variable.input_method_code,
          input: inputVariables,
          test: variable.test
        }
      })

      this.results.variables = this.results.variables.filter(variable => variable.input_method_code !== 'A')
    },
    selectProtocol(p) {
      this.protocol.selected = p

      this.player.selectDialog = true
    },
    selectPlayer(p) {
      this.player.selected = p

      this.prepareTestsInput()

      this.fetchValdMeasurements()

      this.results.dialog = true
    },
    selectTestSession(t) {
      this.events.selected = t

      this.fetchDiagnosticResults()

      this.protocol.selected = t.diagnostic_protocol

      this.player.selectDialog = true
    },
    addNewTestSession() {
      EventBus.$emit(this.$events.OPEN_DIALOG_EVENT_FORM, { eventType: EventType.TEST_SESSION })
    },
    async submit() {
      if (!(await this.$refs.testResultsValidation.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        let resp

        if (this.results.id) {
          resp = await DiagnosticResultApiService.update(this.results.id, {
            player_id: this.player.selected.id,
            event_id: this.events.selected.id,
            diagnostic_protocol_id: this.protocol.selected.id,
            variables: this.results.variables.map(v => {
              return {
                id: v.id,
                input: v.input.map(i => {
                  return {
                    rep: i.rep,
                    value: i.value === undefined ? null : i.value
                  }
                })
              }
            }).filter(v => v.input.some(i => i.value !== null)),
            tests: this.resultVariablesGrouped.map(testGroup => {
              return {
                id: testGroup.test.id,
                data_url: testGroup.data_url
              }
            }).filter(t => t.data_url)
          })

          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.results_save.success'),
            color: 'success'
          })

          this.results.dialog = false
          this.$refs.testResultsValidation.reset()

          EventBus.$emit(this.$events.DIAGNOSTIC_RESULT_UPDATED, resp.data)
        } else {
          resp = await DiagnosticResultApiService.create({
            player_id: this.player.selected.id,
            event_id: this.events.selected.id,
            diagnostic_protocol_id: this.protocol.selected.id,
            variables: this.results.variables.map(v => {
              return {
                id: v.id,
                input: v.input.map(i => {
                  return {
                    rep: i.rep,
                    value: i.value === undefined ? null : i.value
                  }
                })
              }
            }).filter(v => v.input.some(i => i.value !== null)),
            tests: this.resultVariablesGrouped.map(testGroup => {
              return {
                id: testGroup.test.id,
                data_url: testGroup.data_url
              }
            }).filter(t => t.data_url)
          })

          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.results_save.success'),
            color: 'success'
          })

          this.results.dialog = false

          this.selectProtocol(this.protocol.selected)
          this.$refs.testResultsValidation.reset()

          EventBus.$emit(this.$events.DIAGNOSTIC_RESULT_CREATED, resp.data)
        }

        if (this.events.selected) {
          if (!this.events.selected.diagnostic_results) {
            this.events.selected.diagnostic_results = [
              resp.data
            ]
          } else {
            this.events.selected.diagnostic_results.push(resp.data)
          }
        }
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.results_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async destroyPlayerDiagnostics() {
      this.deletingDiagnostics = true

      try {
        await EventApiService.deletePlayerDiagnosticsData(this.events.selected.id, this.player.selected.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.destroy_diagnostics.success'),
          color: 'success'
        })

        this.diagnosticsDeleteConfirmationDialogVisible = false

        this.results.dialog = false

        EventBus.$emit(this.$events.DIAGNOSTIC_RESULT_DELETED)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.destroy_diagnostics.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.deletingDiagnostics = false
      }
    },
    async destroyEventDiagnostics() {
      this.deletingDiagnostics = true

      try {
        await EventApiService.deleteEventDiagnosticsData(this.events.selected.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.destroy_diagnostics.success'),
          color: 'success'
        })

        this.diagnosticsEventDeleteConfirmationDialogVisible = false

        this.results.dialog = false

        EventBus.$emit(this.$events.DIAGNOSTIC_RESULT_DELETED)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.destroy_diagnostics.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.deletingDiagnostics = false
      }
    },
    async fetchDiagnosticResults() {
      try {
        this.diagnosticResults.loading = true

        const result = await DiagnosticResultApiService.get({
          event_id: this.events.selected.id
        })

        this.diagnosticResults.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_diagnostics.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.diagnosticResults.loading = false
      }
    },
    range(min, max, step) {
      const result = []
      for (let i = min; i <= max; i += step) {
        result.push(i)
      }
      return result
    },
    getValdMeasurementsForTest(diagnosticTestId) {
      return this.valdMeasurements.data.filter(v => v.diagnostic_test.test_id === diagnosticTestId)
    }
  }
}
</script>
